import { useState, useEffect } from 'react';

const cache = new Map();

/**
 * Henter indhold fra en CMS-samling via det relative runtime endpoint /_api/public/v2/content/:collectionName
 */
export async function fetchCmsCollection(collectionName, params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `/_api/public/v2/content/${collectionName}${query ? `?${query}` : ''}`;

  if (cache.has(url)) {
    return cache.get(url);
  }

  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Fejl ved hentning af ${collectionName}: ${res.status}`);
    }
    const data = await res.json();
    const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
    cache.set(url, items);
    return items;
  } catch (err) {
    console.warn(`CMS hentningsadvarsel (${collectionName}):`, err);
    return null;
  }
}

/**
 * React hook til at forbruge en CMS-samling med øjeblikkelig fallback til eksisterende data
 */
export function useCmsCollection(collectionName, fallbackData = [], params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `/_api/public/v2/content/${collectionName}${query ? `?${query}` : ''}`;

  const [data, setData] = useState(() => {
    return cache.get(url) || fallbackData;
  });
  const [loading, setLoading] = useState(!cache.has(url));

  useEffect(() => {
    let isMounted = true;
    fetchCmsCollection(collectionName, params).then((items) => {
      if (!isMounted) return;
      if (items && items.length > 0) {
        // Sorter efter sortering hvis feltet findes
        const sorted = [...items].sort((a, b) => {
          const sortA = a.data?.sortering ?? a.sortering ?? 999;
          const sortB = b.data?.sortering ?? b.sortering ?? 999;
          return sortA - sortB;
        });
        setData(sorted);
      } else if (items && items.length === 0) {
        setData([]);
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [collectionName, url]);

  return { data, loading };
}

/**
 * Henter sideindstillinger fra CMS
 */
export function useCmsSettings(fallbackSettings) {
  const { data } = useCmsCollection('sideindstillinger', []);
  if (!data || data.length === 0) return fallbackSettings;

  const raw = data[0].data || data[0];
  return {
    ...fallbackSettings,
    name: raw.foreningsnavn || fallbackSettings.name,
    tagline: raw.tagline || fallbackSettings.tagline,
    hero: {
      ...fallbackSettings.hero,
      title: raw.hero_titel || fallbackSettings.hero?.title,
      description: raw.hero_beskrivelse || fallbackSettings.hero?.description,
      images: Array.isArray(raw.hero_billeder) && raw.hero_billeder.length > 0
        ? raw.hero_billeder
        : fallbackSettings.hero?.images,
    },
    vision: {
      ...fallbackSettings.vision,
      title: raw.vision_titel || fallbackSettings.vision?.title,
      content: [
        raw.vision_tekst_1 || fallbackSettings.vision?.content?.[0],
        raw.vision_tekst_2 || fallbackSettings.vision?.content?.[1],
        raw.vision_tekst_3 || fallbackSettings.vision?.content?.[2],
      ].filter(Boolean),
      image: raw.vision_billede?.[0] || fallbackSettings.vision?.image,
    },
    support: {
      ...fallbackSettings.support,
      title: raw.stoet_titel || fallbackSettings.support?.title,
      description: raw.stoet_beskrivelse || fallbackSettings.support?.description,
      mobilePay: raw.mobilepay_billede?.[0] || fallbackSettings.support?.mobilePay,
    },
    contact: {
      ...fallbackSettings.contact,
      emails: [
        raw.kontakt_email_1 || fallbackSettings.contact?.emails?.[0],
        raw.kontakt_email_2 || fallbackSettings.contact?.emails?.[1],
      ].filter(Boolean),
      phone: raw.kontakt_tlf || fallbackSettings.contact?.phone,
      cvr: raw.cvr || fallbackSettings.contact?.cvr,
    },
  };
}
